import { _ } from "../../../polyglot/polyglot.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import TreeItemWidget from "../TreeItemWidget.js";

/**
 * `TreeWidget` is a lazy implementation of a tree widget. The nodes are
 * built using `treeItemWidget`. It is mainly compatible with jqTree for
 * backward compatibility reasons.
 *
 * A tree widget can have several roots, passed via `spec.data`, in which
 * case roots will be displayed as siblings with no parent.
 *
 * The conversion from model to tree node is purely internal, and nodes
 * should not be exposed.
 *
 * @param {*[]} data - Array of tree roots
 * @param {string} cssClass - Extra CSS class for the root ul of the tree
 * @param {function} [getLabel] - Function which given a model returns the its node label
 * @param {function} [getId] - Function which given a model returns the its node id
 * @param {function} [getChildren] - Function which given a model returns the its children
 * @param {function} [canBeSelected] - Function which given a model returns true if the its node can be selected
 * @param {function} [searchFn] - Function taking a model and a search-string and
 * returns `true` if the model matches the search-string
 * @param {integer} autoOpen - Expand all nodes whose depth is strictly lower than the value
 * @param {string} searchString - String used to search through the tree nodes
 */
export default class TreeWidget extends Widget {
	constructor(spec) {
		super(spec);

		this._data = spec.data;
		this._cssClass = spec.cssClass || "";
		this._getLabel = spec.getLabel || this._defaultGetLabel;
		this._getId = spec.getId || this._defaultGetId;
		this._getChildren = spec.getChildren || this._defaultGetChildren;
		this._canBeSelected = spec.canBeSelected || this._defaultCanBeSelected;
		this._searchFn = spec.searchFn || this._defaultSearchFn;
		this._autoOpen = spec.autoOpen;
		this._searchString = spec.searchString;

		this._selectedNode = null;
		this._rootItemWidgets = null;

		this._resolveChildren();

		this.onSelectionChange = this._events.createEvent();
	}

	setData(data) {
		this._data = data;
		this.updateChildren();
	}

	updateChildren() {
		this._resolveChildren();
		this.update();
	}

	applySearchFrom(search) {
		return search.applySearchOnTree(this);
	}

	renderContentOn(html) {
		const allHidden = this._rootItemWidgets.every(
			(child) => !child.shouldBeRendered(),
		);

		if (allHidden) {
			html.span(
				{
					klass: "empty-search-result",
					"data-test": "empty-search-result-info",
				},
				_("No item matching"),
			);

			return;
		}

		html.ul(
			{
				klass: ["tree", "tree-root", this._cssClass],
			},
			this._rootItemWidgets,
		);
	}

	selectedNode() {
		return this._selectedNode && this._selectedNode.getModel();
	}

	isSelected(model) {
		return (
			this._selectedNode &&
			this._getId(model) === this._getId(this._selectedNode.getModel())
		);
	}

	filterNodes(searchString) {
		this._resetSelection();

		this._rootItemWidgets.forEach((child) =>
			child.filterNodes(searchString),
		);

		this.update();
	}

	resetSearch() {
		this.filterNodes();
	}

	forceUpdateNodeFor(item) {
		this.findNode(item, (node) => node.forceRefresh());
	}

	findNode(item, fn) {
		// Use `some` to keep the laziness at max
		return this._rootItemWidgets.some((child) => child.findNode(item, fn));
	}

	scrollTo(item, fn) {
		return this.findNode(item, fn);
	}

	collapseAll() {
		this._rootItemWidgets.forEach((child) => child.collapseAll());
	}

	expandAll() {
		this._rootItemWidgets.forEach((child) => child.expandAll());
	}

	expandOnly(model) {
		this.collapseAll();
		this.findNode(model, (node) => node.expandAll());
	}

	selectNode(model) {
		return this.scrollTo(model, (node) => this._selectNode(node));
	}

	addChild(parent, item) {
		this.findNode(parent, (node) => node.addChild(item));
	}

	removeNode(model, parent) {
		if (
			this._data.some(
				(child) => this._getId(child) === this._getId(model),
			)
		) {
			this._removeRoot(model);
		} else {
			this._removeNonRoot(model, parent);
		}

		this.update();
	}

	hasSelection() {
		return !!this._selectedNode;
	}

	_removeNonRoot(model, parent) {
		this.findNode(parent, (parentNode) => {
			parentNode.removeNode(model);
			if (this.isSelected(model)) {
				this._selectNode(parentNode);
			}
		});
	}

	_removeRoot(model) {
		this._rootItemWidgets = this._rootItemWidgets.filter(
			(childNode) =>
				this._getId(childNode.getModel()) !== this._getId(model),
		);

		if (this.isSelected(model)) {
			this._resetSelection();
		}
	}

	_defaultGetLabel(item) {
		return item.getName();
	}

	_defaultGetId(item) {
		return item.getId();
	}

	_defaultGetChildren(item) {
		return item.getChildren();
	}

	_defaultCanBeSelected() {
		return true;
	}

	_defaultSearchFn(item, searchString) {
		return this._getLabel(item)
			.toString()
			.toLowerCase()
			.match(searchString.toLowerCase());
	}

	_createItem(item) {
		const widget = new TreeItemWidget({
			parent: null,
			item,
			depth: 0,
			isExpanded: false,
			getLabel: this._getLabel,
			getId: this._getId,
			getChildren: this._getChildren,
			canBeSelected: this._canBeSelected,
			searchFn: this._searchFn,
			autoOpen: this._autoOpen,
			searchString: this._searchString,
		});

		widget.onSelectionChange.register((node) => this._nodeSelected(node));

		return widget;
	}

	_resetSelection() {
		this._nodeSelected(null);
	}

	_resolveChildren() {
		this._rootItemWidgets = this._data.map(this._createItem.bind(this));

		this._rootItemWidgets.forEach((child, index) => {
			child.setPrevious(this._rootItemWidgets[index - 1]);
			child.setNext(this._rootItemWidgets[index + 1]);
		});
	}

	_nodeSelected(node) {
		if (this._selectedNode === node) {
			return;
		}

		if (this._selectedNode) {
			this._selectedNode.deselect();
		}

		this._selectedNode = node;

		this.onSelectionChange.trigger(node && node.getModel(), node);
	}

	_selectNode(node) {
		if (!node) {
			this._resetSelection();
			return;
		}

		node.select();
	}
}
